import Order from "./Order.js";
import HttpClient from "./HttpClient";
import Pallet from "./Pallet.js";
import Stack from "./Stack.js";
import Helper from "./Helper.js";
import Operation from "./Operation.js";
import GlassUnit from "./ProductGlassUnit.js";
import Fitting from "./ProductFitting.js";
import FittingGroup from "./FittingGroup.js";
export default class Product {
  constructor(product) {
    this.id = product.id;
    this.position = product.position;
    this.barcode = product.barcode;
    this.type = product.type;
    this.height = product.height;
    this.width = product.width;
    this.depth = product.depth;
    this.weight = product.weight;
    this.status = product.status;
    this.pallet_id = product.pallet_id;
    this.x = product.x;
    this.y = product.y;
    this.z = product.z;
    this.is_flipped = product.is_flipped;
    this.order_id = product.order_id;
    this.created_at = product.created_at;
    this.updated_at = product.updated_at;
    this.area = product.area;
    this.can_be_flipped = product.can_be_flipped;
    this.comment = product.comment;
    this.part_of = product.part_of;
    this.image = product.image;
    this.product_image = product.product_image;
    this.order = product.order != null ? new Order(product.order) : null;
    this.pallet = (product.pallet) ? new Pallet(product.pallet) : null
    this.divider = product.divider;
    this.operations = this.getOperations(product);
    this.completion_date = product.completion_date;
    this.fitting_groups = this.getFittingGroups(product);
    this.glass_units = this.getGlassUnits(product);
    this.category = product.category;
  }

  getFittingGroups(product) {
    const fittings = this.getFittings(product);
    const fittingGroups = [];
    fittings.forEach(fitting => {
      const foundFittingGroupIndex = fittingGroups.map(e => { return e.code }).indexOf(fitting.code1);
      if (foundFittingGroupIndex == -1) {
        const newFittingGroup = new FittingGroup(fitting.code1);
        newFittingGroup.addFitting(fitting);
        fittingGroups.push(newFittingGroup);
      } else {
        fittingGroups[foundFittingGroupIndex].addFitting(fitting);
      }
    });
    return fittingGroups;
  }

  getFittings(product) {
    const fittings = [];
    if (!product.fittings) {
      return fittings;
    }
    product.fittings.forEach(fitting => {
      fittings.push(new Fitting(fitting));
    });
    return fittings;
  }

  getGlassUnits(product) {
    const glassUnits = [];
    if (!product.glass_units) {
      return glassUnits;
    }
    product.glass_units.forEach(glassUnit => {
      glassUnits.push(new GlassUnit(glassUnit));
    });
    return glassUnits.sort((a, b) => {
      if (a.field < b.field) { return -1; }
      if (a.field > b.field) { return 1; }
      return 0;
    });
  }

  getOperations(product) {
    if (!product.operations) {
      return [];
    }
    const operations = [];
    product.operations.forEach(operation => {
      operations.push(new Operation(operation));
    });
    return operations;
  }

  static async loadProduct(id) {
    const client = new HttpClient();
    const response = await client.instance.get(`products/${id}`);
    return new Product(response.data);
  }

  static async loadByBarcode(barcode) {
    const client = new HttpClient();
    try {
      const response = await client.instance.get(`products/barcode/${barcode}`);
      return new Product(response.data);
    } catch (error) {
      return null;
    }
  }

  getWidth() {
    if (this.is_flipped) {
      return this.height;
    }
    return this.width;
  }

  getHeight() {
    if (this.is_flipped) {
      return this.width;
    }
    return this.height;
  }

  dimensions() {
    const divider = (this.divider) ? this.divider : 1;
    if (this.is_flipped) {
      return `${Math.round(this.height * divider)}x${Math.round(this.width * divider)}`;
    }
    return `${Math.round(this.width * divider)}x${Math.round(this.height * divider)}`;
  }

  toString() {
    return `Poz. ${this.position} ${this.dimensions()}`;
  }

  /**
   * saves changes
   */
  async save(data) {
    const client = new HttpClient;
    const response = await client.instance.put(`products/${this.id}`, data);
    return response.data;
  }

  /**
   * deletes product
   */
  async delete() {
    const client = new HttpClient;
    const response = await client.instance.delete(`products/${this.id}`);
    return response.data;
  }

  /**
   * returns true if product is within pallets
   * @param {Pallet} pallets
   */
  withinPallets(pallets) {
    const found = pallets.map(e => e.id).indexOf(this.pallet_id);
    if (found != -1) {
      return true;
    }
    return false;
  }

  /**
   * gets text that should be printed on a single product
   * @param pallet - pallet on which this product is places
   * @return text to be printed
   */
  getTextOnPallet(pallet) {
    let sideStep = "";
    let flipped = this.is_flipped ? "(verstas)" : "";
    const stack = pallet.products.filter(filteringProduct => {
      return filteringProduct.x === this.x && filteringProduct.y === this.y; //get stack
    });
    const stackAsc = stack.sort((a, b) => {
      return a.z - b.z;
    }); //order stack by z index
    const stackIndex = stackAsc
      .map(e => {
        return e.id;
      })
      .indexOf(this.id); //find z position in stack
    //the higher in stack, the bigger the side step
    sideStep = sideStep + new Array(stackIndex * 5 + 1).join("-");
    //add spaces according to position
    const productionId =
      this.order.production_id != null ? this.order.production_id : "";
    return (
      sideStep +
      `${productionId} Poz. ${
      this.position
      } ${this.getWidth()} x ${this.getHeight()} ${flipped}`
    );
  }

  static getHeaviest(products) {
    let maxWeight = 0;
    products.forEach(product => {
      maxWeight = Math.max(product.weight, maxWeight);
    });
    return maxWeight;
  }

  static async flipProducts(products) {
    products.forEach(product => {
      product.is_flipped = !product.is_flipped;
    });
    await Product.save(products);
    return 1;
  }

  /**
   * moves product to another pallet
   * @param {*} $event
   * @param {array} pallets
   * @param {integer} showingPalletIndex
   */
  static async moveToOtherPallet($event, pallets, showingPalletIndex, text) {
    let tempPallet = null;
    if (!$event.hasOwnProperty("removed")) {
      return;
    }
    const client = new HttpClient();
    //sort pallet products by y
    pallets.forEach(pallet => {
      pallet.products.sort((a, b) => {
        return a.y - b.y;
      });
    });

    let movedProduct = $event.removed.element;
    let movedToIndex = pallets.indexOf(pallets[showingPalletIndex]);
    const movedFromIndex = pallets
      .map(e => {
        return e.id;
      })
      .indexOf(movedProduct.pallet_id);

    let middleOfScreen = true;
    // get if of pallet where product was moved
    pallets.forEach((pallet, index) => {
      pallet.products.forEach(product => {
        if (product["id"] == movedProduct.id) {
          movedToIndex = index;
          middleOfScreen = false;
        }
      });
    });

    if (pallets[movedToIndex].truck_id != null || pallets[movedFromIndex].truck_id != null) {
      return;
    }

    if (pallets[movedToIndex].temporary) {
      const pallet = await Pallet.createPallet(pallets[movedToIndex]);
      pallets[movedToIndex] = new Pallet(pallet);
    }

    if (pallets[movedToIndex].status >= 2 || pallets[movedFromIndex].status >= 2) {
      alert(text);
      return;
    }
    //recalculate the two pallets
    const recalculated = Pallet.recalculatePallets(
      pallets,
      movedFromIndex,
      movedToIndex,
      movedProduct
    );
    await client.instance.post(`products/move/${movedProduct.id}`, {
      moved_from: pallets[movedFromIndex].id,
      moved_to: pallets[movedToIndex].id,
      middle_of_screen: middleOfScreen,
      recalculated_pallets: recalculated
    });
    if (pallets[movedFromIndex].products.length == 0) {
      tempPallet = pallets[movedFromIndex];
      await pallets[movedFromIndex].remove();
      tempPallet.id = Math.floor(Math.random() * Math.floor(9999999));
      tempPallet.temporary = true;
    }
    return tempPallet;
  }

  async select(vue) {
    vue.mouseIsOver = this.id;
    await Helper.timeout(500); // wait to see if mouse is still over
    if (vue.mouseIsOver != this.id) {
      return;
    }
    // if still on selected product
    vue.selectedProduct = this;
    vue.$emit("productSelected", this); // emit change
  }

  static async save(products) {
    const client = new HttpClient();
    const response = await client.instance.put('products', { products: products });
    return response.data;
  }

  static async scan(pallet, barcode) {
    const client = new HttpClient();
    const response = await client.instance.post(`products/scan/${pallet}/${barcode}`);
    return response.data;
  }

  static async create(data) {
    const client = new HttpClient();
    const response = await client.instance.post("products", data);
    return response.data;
  }

  getProductStack(products) {
    const stackProducts = products.filter(e => {
      return e.x == this.x && e.y == this.y;
    });
    const stack = new Stack(stackProducts[0]);
    for (let i = 1; i < stackProducts.length; i++) {
      stack.addToStack(stackProducts[i]);
    }
    return stack
  }

  static async recalculateProducts(products, settings) {
    const adjustedProducts = [];
    products.forEach(product => {
      product.barcode = settings.barcode ? settings.barcode : product.barcode;
      product.position = settings.position ? settings.position : product.position
      product.type = settings.type ? settings.type : product.type;
      product.width = settings.width ? Number.parseInt(settings.width) : product.width;
      product.height = settings.height ? Number.parseInt(settings.height) : product.height;
      product.depth = settings.depth ? Number.parseInt(settings.depth) : product.depth
      product.weight = settings.weight ? Number.parseInt(settings.weight) : product.weight;
      product.area = settings.area ? Number.parseFloat(settings.area) : product.area;
      product.status = settings.status ? Number.parseInt(settings.status) : product.status;
      product.comment = settings.comment ? settings.comment : product.comment;
      product.category = settings.category ? settings.category : product.category;
      adjustedProducts.push(product);
    });
    console.log(adjustedProducts);
    await this.save(adjustedProducts);
  }

  static async saveProductsComment(comment, products) {
    products.forEach((product) => {
      product.comment = comment;
    });
    await this.save(products);
  }

  getWorkCenterOperation(workCenterId) {
    //TODO check if returning the correct operation
    //dublications can appear when returning back
    const operation = this.operations.find(e => {
      return e.work_center_id == workCenterId && e.status != 4;
    });
    return operation;
  }

  getWorkCenterOperations(workCenterId) {
    return this.operations.filter(e => {
      return e.work_center_id == workCenterId;
    });
  }

  operationNotCompleted(workCenterId) {
    //make sure operation is even needed to complete
    const operation = this.getWorkCenterOperation(workCenterId);
    if (!operation) {
      return 0;
    }
    if (operation.status != 2) {
      return 1;
    }
    return 0;
  }

  static async endOperations(workCenterId, products) {
    const client = new HttpClient();
    const response = await client.instance.post(`work-centers/complete/${workCenterId}`, {
      products: products
    });
    return response.data;
  }

  static async recordProblem(workCenterId, products, comment) {
    const client = new HttpClient();
    const response = await client.instance.post(`work-centers/problem/${workCenterId}`, {
      products: products,
      comment: comment
    });
    return response.data;
  }

  /**
   * returns selected products from one work center to another
   * @param {*} workCenterId - return from workCenter
   * @param {*} products - products to return
   * @param {*} comment - comment about problem
   * @param {*} returnTo - return to workCenter
   */
  static async returnOperations(workCenterId, products, returnTo) {
    const client = new HttpClient();
    const response = await client.instance.post(`work-centers/return/${workCenterId}`, {
      products: products,
      return_to: returnTo.id
    });
    return response.data;
  }

  getPreviewComments(workCenterId) {
    let comments = [];
    for (const operation of this.operations) {
      if (operation.work_center_id == workCenterId) {
        comments = [...comments, ...operation.comments]
      }
    }
    return comments;
  }

  getCategory(vue){
    switch(this.category){
      case 0:
        return vue.$t('products_crud.addition')
      case 1:
          return vue.$t('products_crud.window')
      case 2:
        return vue.$t('products_crud.doors')
      default:
        return "-"
    }
  }
}
